import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Check, CheckCircle2, Plus, Loader2, FileCheck2, Package, Phone,
  ChevronLeft, Clock,
} from 'lucide-react';

const READINESS_BG = {
  green: 'bg-emerald-500', amber: 'bg-amber-500',
  red: 'bg-red-500',       grey: 'bg-slate-400',
};

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Foreman Mobile — stripped-down phone-first job control screen.
 * Route: /m/job/:id   (no AppLayout / no sidebar — full bleed)
 * No auth gate beyond reading `rtr_token` from localStorage; if missing,
 * we redirect to /login. Designed for crew leads in a tool belt — single
 * column, big tap targets, all live data in one bundle.
 */
export default function ForemanMobilePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [bundle, setBundle] = useState(null);
  const [busy, setBusy] = useState(false);
  const [newRow, setNewRow] = useState({ date: '', start: '', finish: '', comments: '' });
  const [marking, setMarking] = useState(false);
  const [completed, setCompleted] = useState(false);

  const apiAuth = useCallback(() => {
    return { withCredentials: true };
  }, [navigate]);

  const load = useCallback(async () => {
    const cfg = apiAuth(); if (!cfg) return;
    try {
      const r = await axios.get(`${API_BASE}/appraisals/${id}/foreman-bundle`, cfg);
      setBundle(r.data);
      if (r.data?.status === 'Completed') setCompleted(true);
      // Default today's date for the new-row pad
      const today = new Date().toISOString().slice(0, 10);
      setNewRow(s => ({ ...s, date: today }));
    } catch (e) {
      console.error('Foreman bundle load failed', e);
    }
  }, [id, apiAuth]);

  useEffect(() => { load(); }, [load]);

  const toggleItem = async (ref, nextValue) => {
    const cfg = apiAuth(); if (!cfg) return;
    // Optimistic update
    setBundle(b => b && ({ ...b, items: b.items.map(it => it.ref === ref ? { ...it, completed: nextValue } : it) }));
    try {
      await axios.patch(`${API_BASE}/appraisals/${id}/completed-items`,
        { ref, completed: nextValue }, cfg);
    } catch (e) {
      console.error('toggle failed', e);
      // Roll back
      setBundle(b => b && ({ ...b, items: b.items.map(it => it.ref === ref ? { ...it, completed: !nextValue } : it) }));
    }
  };

  const addWorkRow = async () => {
    if (!newRow.date && !newRow.start && !newRow.finish && !newRow.comments) return;
    const cfg = apiAuth(); if (!cfg) return;
    setBusy(true);
    const updated = [...(bundle?.work_history || []), newRow];
    try {
      await axios.patch(`${API_BASE}/appraisals/${id}/work-history`,
        { rows: updated }, cfg);
      setBundle(b => b && ({ ...b, work_history: updated }));
      setNewRow({
        date: new Date().toISOString().slice(0, 10),
        start: '', finish: '', comments: '',
      });
    } catch (e) {
      alert('Failed to add row');
    }
    setBusy(false);
  };

  const markCompleted = async () => {
    if (!window.confirm('Mark this job as Completed? The office will be prompted to issue the Certificate of Completion.')) return;
    const cfg = apiAuth(); if (!cfg) return;
    setMarking(true);
    try {
      await axios.patch(`${API_BASE}/appraisals/${id}/mark-completed`, {}, cfg);
      setCompleted(true);
      setBundle(b => b && ({ ...b, status: 'Completed' }));
    } catch (e) {
      alert(e?.response?.data?.detail || 'Failed to mark completed');
    }
    setMarking(false);
  };

  if (!bundle) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="animate-spin text-slate-400" size={32} />
      </div>
    );
  }

  const r = bundle.readiness || {};
  const arrivedDot = READINESS_BG[r.status] || READINESS_BG.grey;
  const ticked = (bundle.items || []).filter(i => i.completed).length;
  const totalItems = (bundle.items || []).length;

  return (
    <div data-testid="foreman-mobile-page" className="min-h-screen bg-slate-50 pb-32">
      {/* Top bar */}
      <header className="bg-slate-900 text-white px-4 py-3 flex items-center gap-3 sticky top-0 z-10 shadow">
        <button onClick={() => navigate(-1)} data-testid="foreman-back"
                className="p-1.5 rounded hover:bg-slate-800">
          <ChevronLeft size={20} />
        </button>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.18em] text-slate-400">Foreman Mode</div>
          <h1 className="text-base font-bold truncate">Job #{bundle.appraisal_no || id.slice(-6)}</h1>
        </div>
        {bundle.client_phone && (
          <a href={`tel:${bundle.client_phone}`} data-testid="foreman-call-client"
             className="px-3 py-1.5 bg-emerald-600 rounded text-xs font-semibold flex items-center gap-1.5">
            <Phone size={13} /> Call
          </a>
        )}
      </header>

      {/* Job header card */}
      <section className="bg-white border-b border-slate-200 px-4 py-4">
        <div className="text-xs text-slate-500 mb-0.5">{bundle.quote_type || '—'}</div>
        <div className="text-lg font-bold text-slate-900 leading-tight">{bundle.client_name}</div>
        <div className="text-sm text-slate-600">{bundle.property_address}</div>
        <div className="mt-3 flex items-center gap-2 flex-wrap">
          <span data-testid="foreman-readiness-badge"
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-slate-100 text-xs font-semibold text-slate-700">
            <span className={`w-2 h-2 rounded-full ${arrivedDot}`} />
            <Package size={12} />
            {r.total > 0 ? `${r.arrived}/${r.total} materials arrived` : 'No materials'}
          </span>
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-slate-100 text-xs font-semibold text-slate-700">
            <CheckCircle2 size={12} />
            {ticked}/{totalItems} ticked
          </span>
          {bundle.status && (
            <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-semibold ${
              bundle.status === 'Completed' ? 'bg-emerald-100 text-emerald-800' :
              'bg-slate-100 text-slate-700'
            }`}>{bundle.status}</span>
          )}
        </div>
      </section>

      {/* Job Status Check List */}
      <section className="bg-white border-b border-slate-200 mt-2 px-4 py-3">
        <h2 className="text-xs font-bold uppercase tracking-[0.18em] text-slate-500 mb-3">Job checklist · tap to tick</h2>
        <ul className="divide-y divide-slate-100">
          {(bundle.items || []).map(item => (
            <li key={item.ref || item.description} data-testid={`foreman-item-${item.ref}`}>
              <button
                onClick={() => toggleItem(item.ref, !item.completed)}
                className="w-full flex items-start gap-3 py-3 px-1 text-left active:bg-slate-50 rounded transition-colors"
              >
                <span className={`mt-0.5 w-6 h-6 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                  item.completed ? 'bg-emerald-500 border-emerald-500' : 'bg-white border-slate-300'
                }`}>
                  {item.completed && <Check size={16} className="text-white" />}
                </span>
                <div className="flex-1 min-w-0">
                  <div className={`text-sm font-medium ${item.completed ? 'text-slate-400 line-through' : 'text-slate-800'}`}>
                    {item.description}
                  </div>
                  {item.category && <div className="text-[10px] text-slate-400 uppercase tracking-wider mt-0.5">{item.category}</div>}
                </div>
              </button>
            </li>
          ))}
          {(!bundle.items || bundle.items.length === 0) && (
            <li className="py-6 text-center text-sm text-slate-400">No line items quoted yet.</li>
          )}
        </ul>
      </section>

      {/* Work History */}
      <section className="bg-white border-b border-slate-200 mt-2 px-4 py-3">
        <h2 className="text-xs font-bold uppercase tracking-[0.18em] text-slate-500 mb-3">Work history</h2>
        <div className="space-y-2 mb-4">
          {(bundle.work_history || []).map((row, i) => (
            <div key={i} className="rounded-md bg-slate-50 px-3 py-2 text-sm">
              <div className="flex items-center gap-3 text-slate-700 font-medium">
                <Clock size={13} className="text-slate-400" />
                <span>{row.date || '—'}</span>
                <span className="text-slate-400">·</span>
                <span>{row.start || '—'} → {row.finish || '—'}</span>
              </div>
              {row.comments && <div className="mt-1 text-xs text-slate-600">{row.comments}</div>}
            </div>
          ))}
          {(!bundle.work_history || bundle.work_history.length === 0) && (
            <div className="text-xs text-slate-400 italic">No history rows yet — add the first one below.</div>
          )}
        </div>

        {/* Add row pad */}
        <div className="rounded-md border border-slate-200 p-3 bg-slate-50/40 space-y-2">
          <div className="grid grid-cols-3 gap-2">
            <input data-testid="foreman-row-date" type="date" value={newRow.date}
                   onChange={e => setNewRow(s => ({ ...s, date: e.target.value }))}
                   className="col-span-3 px-3 py-2 border border-slate-200 rounded-md text-sm bg-white" />
            <input data-testid="foreman-row-start" type="time" value={newRow.start}
                   onChange={e => setNewRow(s => ({ ...s, start: e.target.value }))}
                   placeholder="Start"
                   className="col-span-1 px-3 py-2 border border-slate-200 rounded-md text-sm bg-white" />
            <input data-testid="foreman-row-finish" type="time" value={newRow.finish}
                   onChange={e => setNewRow(s => ({ ...s, finish: e.target.value }))}
                   placeholder="Finish"
                   className="col-span-1 px-3 py-2 border border-slate-200 rounded-md text-sm bg-white" />
            <button data-testid="foreman-row-add"
                    onClick={addWorkRow} disabled={busy}
                    className="col-span-1 px-3 py-2 bg-slate-900 text-white rounded-md text-xs font-semibold flex items-center justify-center gap-1 disabled:opacity-50">
              {busy ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />} Add
            </button>
          </div>
          <textarea data-testid="foreman-row-comments" rows={2} value={newRow.comments}
                    onChange={e => setNewRow(s => ({ ...s, comments: e.target.value }))}
                    placeholder="Comments (e.g. removed old ridges, pointed bed coat)"
                    className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm bg-white resize-none" />
        </div>
      </section>

      {/* Mark Completed — fixed bottom bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 px-4 py-3 shadow-[0_-4px_12px_rgba(0,0,0,0.04)]">
        {completed ? (
          <div data-testid="foreman-completed-banner"
               className="w-full flex items-center gap-3 px-4 py-3 bg-emerald-50 rounded-md text-emerald-700 text-sm font-semibold">
            <FileCheck2 size={20} />
            Job marked complete · office prompted to issue the Certificate.
          </div>
        ) : (
          <button data-testid="foreman-mark-completed-btn"
                  onClick={markCompleted}
                  disabled={marking || ticked < totalItems}
                  className="w-full px-4 py-3.5 rounded-md bg-[#0F4F1B] text-white font-bold text-sm disabled:opacity-50 flex items-center justify-center gap-2"
                  title={ticked < totalItems ? `${totalItems - ticked} item(s) still to tick` : 'Mark this job as Completed'}>
            {marking ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={18} />}
            {marking ? 'Marking…' : ticked < totalItems
              ? `${totalItems - ticked} item(s) left to tick`
              : 'Mark Job Completed'}
          </button>
        )}
      </div>
    </div>
  );
}
